// src/pages/PropertyDetailPage.js
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getProperty, sendEnquiry } from "../utils/api";
import { useAuth } from "../context/AuthContext";
import Badges from "../components/Badges";
import Spinner from "../components/Spinner";
import { WHATSAPP_MSG } from "../utils/constants";
import toast from "react-hot-toast";

const fmt = (n) => {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000)   return `₹${(n / 100000).toFixed(1)} Lakh`;
  return `₹${n?.toLocaleString("en-IN")}`;
};

export default function PropertyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [property, setProperty] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [enquiryMsg, setEnquiryMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [showEnquiry, setShowEnquiry] = useState(false);

  useEffect(() => {
    getProperty(id)
      .then(p => { setProperty(p); })
      .catch(() => toast.error("Property not found"))
      .finally(() => setLoading(false));
  }, [id]);

  const handleEnquiry = async () => {
    if (!user) { navigate("/login"); return; }
    if (!enquiryMsg.trim()) { toast.error("Please enter a message"); return; }
    setSending(true);
    try {
      await sendEnquiry(id, user.uid, property.ownerId, enquiryMsg);
      toast.success("Enquiry sent to the owner!");
      setEnquiryMsg("");
      setShowEnquiry(false);
    } catch {
      toast.error("Failed to send enquiry. Try again.");
    } finally {
      setSending(false);
    }
  };

  if (loading) return <Spinner />;
  if (!property) return (
    <div style={{ textAlign: "center", padding: 60 }}>
      <p style={{ fontSize: 48 }}>🏚️</p>
      <p style={{ fontWeight: 700, fontSize: 18, marginTop: 12 }}>Property not found</p>
      <button onClick={() => navigate("/properties")} style={{ marginTop: 16, padding: "10px 22px", background: "#1a6b3c", color: "white", border: "none", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>
        Browse Properties
      </button>
    </div>
  );

  const images = property.images?.length ? property.images : ["https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=800&q=80"];
  const waLink = `https://wa.me/91${property.contact}?text=${encodeURIComponent(WHATSAPP_MSG(property.title))}`;

  return (
    <div style={{ maxWidth: 960, margin: "0 auto", padding: "16px" }}>
      {/* Back */}
      <button onClick={() => navigate(-1)} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", color: "#1a6b3c", fontWeight: 600, cursor: "pointer", marginBottom: 16, fontFamily: "inherit", fontSize: 14 }}>
        ← Back to Listings
      </button>

      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16 }}>

        {/* ── IMAGE GALLERY ── */}
        <div style={{ background: "white", borderRadius: 16, overflow: "hidden", border: "1px solid #e2ece2" }}>
          <div style={{ position: "relative" }}>
            <img src={images[activeImg]} alt={property.title}
              style={{ width: "100%", height: "clamp(220px,45vw,420px)", objectFit: "cover", display: "block" }} />
            {/* Badges overlay */}
            <div style={{ position: "absolute", top: 12, left: 12, display: "flex", gap: 6, flexWrap: "wrap" }}>
              <span style={{ background: "#1a6b3c", color: "white", fontSize: 12, fontWeight: 700, padding: "4px 10px", borderRadius: 20 }}>{property.type}</span>
              {property.urgent && <span style={{ background: "#c0392b", color: "white", fontSize: 11, fontWeight: 700, padding: "4px 9px", borderRadius: 20 }}>🔥 Urgent Sale</span>}
            </div>
            {property.verified && (
              <div style={{ position: "absolute", top: 12, right: 12 }}>
                <span style={{ background: "#e8f5e9", color: "#1a6b3c", fontSize: 11, fontWeight: 700, padding: "4px 9px", borderRadius: 20 }}>✓ Verified</span>
              </div>
            )}
            {/* Arrow nav */}
            {images.length > 1 && (
              <>
                <button onClick={() => setActiveImg(i => (i - 1 + images.length) % images.length)}
                  style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", background: "rgba(0,0,0,0.45)", border: "none", color: "white", width: 34, height: 34, borderRadius: 17, cursor: "pointer", fontSize: 16 }}>‹</button>
                <button onClick={() => setActiveImg(i => (i + 1) % images.length)}
                  style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "rgba(0,0,0,0.45)", border: "none", color: "white", width: 34, height: 34, borderRadius: 17, cursor: "pointer", fontSize: 16 }}>›</button>
                <div style={{ position: "absolute", bottom: 10, right: 12, background: "rgba(0,0,0,0.5)", color: "white", fontSize: 12, padding: "3px 8px", borderRadius: 10 }}>
                  {activeImg + 1}/{images.length}
                </div>
              </>
            )}
          </div>
          {/* Thumbnails */}
          {images.length > 1 && (
            <div style={{ display: "flex", gap: 6, padding: "10px 14px", overflowX: "auto" }}>
              {images.map((img, i) => (
                <img key={i} src={img} alt="" onClick={() => setActiveImg(i)}
                  style={{ width: 64, height: 48, objectFit: "cover", borderRadius: 7, cursor: "pointer", border: activeImg === i ? "2.5px solid #1a6b3c" : "2px solid transparent", flexShrink: 0 }} />
              ))}
            </div>
          )}
        </div>

        {/* ── DETAILS ── */}
        <div style={{ background: "white", borderRadius: 16, padding: "20px", border: "1px solid #e2ece2" }}>
          <Badges property={property} size="md" />
          <h1 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: "clamp(18px,3.5vw,26px)", color: "#1a2e1a", margin: "10px 0 6px", lineHeight: 1.3 }}>
            {property.title}
          </h1>
          <p style={{ display: "flex", alignItems: "center", gap: 5, color: "#6b7c6b", fontSize: 14, marginBottom: 14 }}>
            📍 {property.location}, Satna, Madhya Pradesh
          </p>
          <p style={{ fontSize: 30, fontWeight: 800, color: "#1a6b3c", fontFamily: "'Sora',sans-serif", marginBottom: 16 }}>
            {fmt(property.price)}
            {property.listingType === "Rent" && <span style={{ fontSize: 16, fontWeight: 500 }}>/month</span>}
          </p>

          {/* Specs grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(110px,1fr))", gap: 10, marginBottom: 18, padding: 14, background: "#f8faf8", borderRadius: 10 }}>
            {[
              property.area      && ["📐", "Area",      `${property.area} sq.ft`],
              property.bedrooms  && ["🛏",  "Bedrooms",  `${property.bedrooms} BHK`],
              property.bathrooms && ["🚿",  "Bathrooms", property.bathrooms],
              property.facing    && ["🧭",  "Facing",    property.facing],
              property.furnishing && ["🪑", "Furnishing", property.furnishing],
              property.floor     && ["🏢",  "Floor",     property.floor],
            ].filter(Boolean).map(([icon, label, val]) => (
              <div key={label} style={{ textAlign: "center" }}>
                <p style={{ fontSize: 18, marginBottom: 2 }}>{icon}</p>
                <p style={{ fontSize: 11, color: "#6b7c6b" }}>{label}</p>
                <p style={{ fontSize: 13, fontWeight: 700, color: "#1a2e1a" }}>{val}</p>
              </div>
            ))}
          </div>

          {/* Description */}
          {property.description && (
            <>
              <h3 style={{ fontWeight: 700, fontSize: 15, color: "#1a2e1a", marginBottom: 7 }}>About this property</h3>
              <p style={{ color: "#6b7c6b", fontSize: 14, lineHeight: 1.75, marginBottom: 18 }}>{property.description}</p>
            </>
          )}

          {/* Owner info strip */}
          <div style={{ background: "#f0fff4", border: "1px solid #c3e6cb", borderRadius: 10, padding: "12px 14px", marginBottom: 18, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22 }}>👤</span>
            <div>
              <p style={{ fontSize: 13, fontWeight: 700, color: "#1a2e1a" }}>
                {property.ownerOnly ? "Direct Owner Listing — Zero Brokerage" : "Listed by Agent / Dealer"}
              </p>
              <p style={{ fontSize: 12, color: "#1a6b3c" }}>
                {property.verified ? "✓ Verified listing by ApnaSatna.com" : "Pending verification"}
              </p>
            </div>
          </div>

          {/* Contact buttons */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <a href={waLink} target="_blank" rel="noreferrer" style={{
              flex: 1, minWidth: 150, display: "flex", alignItems: "center", justifyContent: "center",
              gap: 8, background: "#25D366", color: "white", padding: "13px 16px",
              borderRadius: 10, fontWeight: 700, fontSize: 15, textDecoration: "none"
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              WhatsApp
            </a>
            <a href={`tel:+91${property.contact}`} style={{
              flex: 1, minWidth: 150, display: "flex", alignItems: "center", justifyContent: "center",
              gap: 8, background: "#1a6b3c", color: "white", padding: "13px 16px",
              borderRadius: 10, fontWeight: 700, fontSize: 15, textDecoration: "none"
            }}>
              📞 Call Now
            </a>
          </div>

          {/* Enquiry form */}
          <button onClick={() => setShowEnquiry(s => !s)} style={{
            width: "100%", marginTop: 10, padding: "11px", borderRadius: 10,
            border: "1.5px solid #1a6b3c", background: "white", color: "#1a6b3c",
            fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "inherit"
          }}>
            {showEnquiry ? "✕ Close" : "✉️ Send Enquiry Message"}
          </button>
          {showEnquiry && (
            <div style={{ marginTop: 10 }}>
              <textarea value={enquiryMsg} onChange={e => setEnquiryMsg(e.target.value)} rows={3}
                placeholder="I'm interested in this property. Please share more details..."
                style={{ width: "100%", padding: "10px 14px", border: "1.5px solid #e2ece2", borderRadius: 8, fontFamily: "inherit", fontSize: 14, resize: "vertical", color: "#1a2e1a" }} />
              <button onClick={handleEnquiry} disabled={sending} style={{
                width: "100%", marginTop: 8, padding: "11px", borderRadius: 9,
                background: "#1a6b3c", color: "white", border: "none", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit"
              }}>
                {sending ? "Sending..." : "Send Enquiry"}
              </button>
            </div>
          )}

          {/* Stats */}
          <div style={{ display: "flex", gap: 16, marginTop: 16, padding: "10px 0", borderTop: "1px solid #e2ece2", fontSize: 12, color: "#6b7c6b" }}>
            <span>👁 {property.views || 0} views</span>
            <span>✉️ {property.enquiries || 0} enquiries</span>
            <span>📅 Posted {property.createdAt?.toDate ? new Date(property.createdAt.toDate()).toLocaleDateString("en-IN") : "recently"}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
